import { IconMap } from '@shared/common/icons';
import { ClinicianDashboardRoutes } from '@shared/common/routing/clinician-dashboard-routes';
import { IRouteStructure } from '@shared/common/routing/IRouteStructure';

export type AdminAccessRole =
  | 'admin'
  | 'user-admin'
  | 'developer'
  | 'translator'
  | 'knowledge-center-admin'
  | 'symptom-admin';

export type AdminNavigationSection =
  | 'ai-ops'
  | 'admin-ops'
  | 'content-admin'
  | 'localization'
  | 'clinical-admin'
  | 'platform';

export interface AdminNavigationItem extends IRouteStructure {
  requiredRoles: AdminAccessRole[];
}

export const ADMIN_NAVIGATION_ITEMS: AdminNavigationItem[] = [
  {
    ...ClinicianDashboardRoutes.AI_ADMIN.view,
    translationDefault: 'Report Studio',
    section: 'ai-ops',
    requiredRoles: ['admin', 'developer'],
  },
  {
    ...ClinicianDashboardRoutes.USER_ADMIN.list,
    translationDefault: 'User admin',
    section: 'admin-ops',
    requiredRoles: ['user-admin'],
  },
  {
    ...ClinicianDashboardRoutes.ORGANISATIONS.view,
    translationDefault: 'Organisations',
    section: 'admin-ops',
    requiredRoles: ['admin'],
  },
  {
    ...ClinicianDashboardRoutes.DEVELOPER.supportCases,
    translationDefault: 'Support cases',
    section: 'admin-ops',
    requiredRoles: ['developer'],
  },
  {
    ...ClinicianDashboardRoutes.FORMS.view,
    section: 'content-admin',
    requiredRoles: ['admin'],
  },
  {
    type: 'url',
    localPath: '',
    fullUrl: `/cn/onboarding-cfg`,
    localeKey: 'Journey',
    translationDefault: 'Journey',
    icon: IconMap.mapMarkerPath,
    section: 'content-admin',
    requiredRoles: ['admin'],
  },
  {
    type: 'url',
    localPath: '',
    fullUrl: `/cn/consent-documents`,
    localeKey: 'ConsentDocuments',
    translationDefault: 'Legal Terms',
    icon: IconMap.scaleBalance,
    section: 'content-admin',
    requiredRoles: ['admin'],
  },
  {
    type: 'url',
    localPath: '',
    fullUrl: `/cn/prescriptions/admin`,
    localeKey: 'Prescriptions',
    translationDefault: 'Prescriptions admin',
    icon: IconMap.pillMultiple,
    section: 'content-admin',
    requiredRoles: ['admin'],
  },
  {
    ...ClinicianDashboardRoutes.SUPPORT_CATALOG.view,
    translationDefault: 'Support catalog',
    section: 'content-admin',
    requiredRoles: ['admin'],
  },
  {
    type: 'url',
    localPath: '',
    fullUrl: `/cn/translation`,
    localeKey: 'Translation',
    translationDefault: 'Translations',
    icon: 'mdi-translate',
    section: 'localization',
    requiredRoles: ['translator'],
  },
  {
    type: 'url',
    localPath: '',
    fullUrl: `/cn/articles`,
    localeKey: 'Library',
    translationDefault: 'Library',
    icon: IconMap.bookOpenPageVariantOutline,
    section: 'clinical-admin',
    requiredRoles: ['knowledge-center-admin'],
  },
  {
    type: 'url',
    localPath: '',
    fullUrl: `/cn/symptoms`,
    localeKey: 'Symptoms',
    translationDefault: 'Symptoms',
    icon: 'mdi-stethoscope',
    section: 'clinical-admin',
    requiredRoles: ['symptom-admin', 'admin'],
  },
  {
    type: 'url',
    localPath: '',
    fullUrl: `/cn/patient-support`,
    localeKey: 'PatientSupport',
    translationDefault: 'Patient Support',
    icon: IconMap.handHeartOutline,
    section: 'clinical-admin',
    requiredRoles: ['admin'],
  },
  {
    ...ClinicianDashboardRoutes.INT_MONITORING.view,
    section: 'platform',
    requiredRoles: ['admin'],
  },
  {
    ...ClinicianDashboardRoutes.LOG_EXPLORER.view,
    translationDefault: 'Log Explorer',
    section: 'platform',
    requiredRoles: ['admin'],
  },
  {
    ...ClinicianDashboardRoutes.UI_COMPONENTS.view,
    translationDefault: 'UI Components',
    section: 'platform',
    requiredRoles: ['admin'],
  },
  {
    ...ClinicianDashboardRoutes.DEVELOPER.migrationCommandCenter,
    translationDefault: 'Migration command center',
    section: 'platform',
    requiredRoles: ['developer'],
  },
  {
    type: 'url',
    localPath: '',
    fullUrl: `/cn/demo-sandbox`,
    localeKey: 'DemoSandbox',
    translationDefault: 'Demo Sandbox',
    icon: IconMap.play,
    section: 'platform',
    requiredRoles: ['admin', 'developer', 'user-admin'],
  },
];

export function getAccessibleAdminNavigationItems(
  hasRole: (role: AdminAccessRole) => boolean,
): AdminNavigationItem[] {
  return ADMIN_NAVIGATION_ITEMS.filter(item => item.requiredRoles.some(role => hasRole(role)));
}

export function getFirstAccessibleAdminNavigationItem(
  hasRole: (role: AdminAccessRole) => boolean,
): AdminNavigationItem | null {
  return getAccessibleAdminNavigationItems(hasRole)[0] ?? null;
}

export function isAdminNavigationUrl(url: string): boolean {
  const normalizedUrl = normalizeUrl(url);

  return ADMIN_NAVIGATION_ITEMS.some(item => {
    const itemUrl = normalizeUrl(item.fullUrl as string);
    return normalizedUrl === itemUrl || normalizedUrl.startsWith(`${itemUrl}/`);
  });
}

function normalizeUrl(url: string): string {
  return (url ?? '').split('?')[0].split('#')[0];
}
